import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';

import { IRootState } from 'config/redux';
import { CreditService } from 'services';
import { AppConstants, RouteConstants, ServiceConstants } from '../../../../constants';
import { AppUtils } from 'utils';
import { CreditResponse, ICreditRequestUpdate } from 'types';
import { SectionTitle, Stepper } from 'components';
import { useCreditRequestForm } from 'context';
import CreditInformationStep from './credit-information';
import PersonalInformationStep from './personal-information';
import IncomeInformationStep from './income-information';
import OutcomeInformationStep from './outcome-information';
import CreditRequestInfoData from 'pages/credit-request/info';

const {
  CREDIT_CONSTANT_C_LOWER,
  CREDIT_CONSTANT_C_HIGHER,
  CREDIT_MONTHLY_INSTALMENT_TIMEOUT,
  CREDIT_REQUEST_STEP_FORM,
  CREDIT_REQUEST_FORM_STEPS,
} = AppConstants;
const { ROUTE_CREDIT_REQUEST_DOCUMENTATION, ROUTE_CREDIT_REQUEST_EXISTS } = RouteConstants;
const {
  REQUEST_CREDIT_UNREQUESTED_ID,
  REQUEST_CREDIT_REQUEST_ID,
  REQUEST_CREDIT_CREATE_ID,
  REQUEST_CREDIT_MULTIPLE_UPDATE_ID,
  REQUEST_CREDIT_SUBMIT_ID,
} = ServiceConstants;

const CreditRequestFormPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid');
  const { options, setActiveStep, setCurrentCredit, isEditable, setIsEditable } = useCreditRequestForm();
  const unrequestedServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_UNREQUESTED_ID]);
  const getServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_REQUEST_ID]);
  const createServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_CREATE_ID]);
  const updateFieldsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_MULTIPLE_UPDATE_ID]);
  const submitServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_SUBMIT_ID]);

  const [activeFormStep, setActiveFormStep] = useState<number>(0);
  const [, setReload] = useState<number>(0);

  const timeouts = useRef<any>({});
  const shouldUpdateFields = useRef<boolean>(false);

  const form = useForm({
    initialValues: {} as CreditResponse,
    onValuesChange: (values, previousValues) => {
      const changedFields = AppUtils.getChangedFieldsForCredit(values, previousValues);

      handleCreditTypeDependencies(changedFields);
      handleBankDependencies(changedFields);
      handleNationalityDependencies(changedFields);
      handleAddressDependencies(changedFields);

      if (!AppUtils.isEmpty(changedFields) && shouldUpdateFields.current) {
        let forcedTimeout;
        if (changedFields.some((field) => field.name === 'creditAmount') || changedFields.some((field) => field.name === 'paymentPeriod')) {
          const minLimit = AppUtils.getCreditLimit(values.creditAmount, values.paymentPeriod, CREDIT_CONSTANT_C_LOWER);
          const maxLimit = AppUtils.getCreditLimit(values.creditAmount, values.paymentPeriod, CREDIT_CONSTANT_C_HIGHER);

          changedFields.push({ name: 'monthlyInstallmentFrom', value: minLimit });
          changedFields.push({ name: 'monthlyInstallmentTo', value: maxLimit });
          forcedTimeout = CREDIT_MONTHLY_INSTALMENT_TIMEOUT;
        }
        scheduleFieldUpdates(values.identification, changedFields, forcedTimeout);
      }
    }
  });

  useEffect(() => {
    setActiveStep(CREDIT_REQUEST_STEP_FORM);
    if (AppUtils.isEmpty(cid)) {
      CreditService.getUnrequested();
    } else {
      CreditService.getRequest(cid!);
    }
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      unrequestedServiceObject,
      REQUEST_CREDIT_UNREQUESTED_ID,
      (val) => {
        if (val) {
          navigate(ROUTE_CREDIT_REQUEST_EXISTS);
        } else {
          CreditService.createRequest(location.state?.initialCreditData);
        }
      }
    );
  }, [unrequestedServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      getServiceObject,
      REQUEST_CREDIT_REQUEST_ID,
      (val) => {
        setValues(val);
        if (val.requestedAt) {
          setIsEditable(false);
          shouldUpdateFields.current = false;
        } else {
          shouldUpdateFields.current = true;
        }
        setReload(old => old + 1);
      }
    );
  }, [getServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(createServiceObject, REQUEST_CREDIT_CREATE_ID,
      (val) => {
        const url = new URL(window.location.href);
        url.searchParams.set('cid', val.identification);
        window.history.pushState(null, '', url.toString());
        setValues(val);
        shouldUpdateFields.current = true;
        setReload(old => old + 1);
      }
    );
  }, [createServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(updateFieldsServiceObject, REQUEST_CREDIT_MULTIPLE_UPDATE_ID,
      (val) => {
        setValues(toObject(val));
      }
    );
  }, [updateFieldsServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(submitServiceObject, REQUEST_CREDIT_CREATE_ID,
      () => {
        setCurrentCredit({
          id: form.values.id,
          creditAmount: form.values.creditAmount,
          paymentPeriod: form.values.paymentPeriod,
          monthlyInstallmentTo: form.values.monthlyInstallmentTo,
          status: 'PROCESSING',
          requestedAt: new Date(),
          user: form.values.user,
        });
        navigate(`${ROUTE_CREDIT_REQUEST_DOCUMENTATION}?cid=${form.values.identification}`);
      },
    );
  }, [submitServiceObject]);

  function toObject(entries: ICreditRequestUpdate[]): Partial<CreditResponse> {
    return entries.reduce((acc, { name, value }) => {
      acc[name] = value;
      return acc;
    }, {} as Partial<CreditResponse>);
  }

  const setValues = useCallback((val: CreditResponse | Partial<CreditResponse>) => {
    const updates: Partial<CreditResponse> = { ...val };

    if ('birthDate' in val) {
      updates.birthDate = val.birthDate ? new Date(val.birthDate) : undefined;
    }

    if ('partnerBirthDate' in val) {
      updates.partnerBirthDate = val.partnerBirthDate ? new Date(val.partnerBirthDate) : undefined;
    }

    if ('visaFrom' in val) {
      updates.visaFrom = val.visaFrom ? new Date(val.visaFrom) : undefined;
    }

    if ('partnerVisaFrom' in val) {
      updates.partnerVisaFrom = val.partnerVisaFrom ? new Date(val.partnerVisaFrom) : undefined;
    }

    if ('livingFrom' in val) {
      updates.livingFrom = val.livingFrom ? new Date(val.livingFrom) : undefined;
    }

    form.setValues(updates);
  }, []);

  function handleCreditTypeDependencies(changedFields: ICreditRequestUpdate[]) {
    const creditTypeField = changedFields.find(field => field.name === 'creditType');

    if (!creditTypeField) {
      return;
    }

    const commonNew: readonly (keyof CreditResponse)[] = [
      'existingMonthlyInstallment',
      'bank',
      'bankName',
      'refinanceCreditCard',
      'refinanceLease'
    ];
    const commonRefinancing: readonly (keyof CreditResponse)[] = ['creditPurpose', 'creditPurposeNote'];

    const creditTypeValue = creditTypeField.value;
    if (!creditTypeValue) {
      [...commonNew, ...commonRefinancing].forEach(name =>
        changedFields.push({ name, value: undefined })
      );
    } else if (creditTypeValue === 'NEW') {
      commonNew.forEach(name =>
        changedFields.push({ name, value: undefined })
      );
    } else if (creditTypeValue === 'REFINANCING') {
      commonRefinancing.forEach(name =>
        changedFields.push({ name, value: undefined })
      );
    }
  }

  function handleBankDependencies(changedFields: ICreditRequestUpdate[]) {
    const bankField = changedFields.find(field => field.name === 'bank');
    const bankValue = bankField?.value;

    if (bankField && bankValue !== undefined && bankValue !== 'OTHER') {
      changedFields.push({ name: 'bankName', value: undefined });
    }
  }

  function handleNationalityDependencies(changedFields: ICreditRequestUpdate[]) {
    const nationalityField = changedFields.find(field => field.name === 'nationality');
    const nationalityValue = nationalityField?.value;

    if (nationalityField && nationalityValue !== 'OTHER') {
      changedFields.push({ name: 'visaType', value: undefined });
      changedFields.push({ name: 'visaFrom', value: undefined });
    }

    const partnerNationalityField = changedFields.find(field => field.name === 'partnerNationality');
    const partnerNationalityValue = partnerNationalityField?.value;

    if (partnerNationalityField && partnerNationalityValue !== 'OTHER') {
      changedFields.push({ name: 'partnerVisaType', value: undefined });
      changedFields.push({ name: 'partnerVisaFrom', value: undefined });
    }
  }

  function handleAddressDependencies(changedFields: ICreditRequestUpdate[]) {
    const livingFromField = changedFields.find(field => field.name === 'livingFrom');
    const livingFromValue = livingFromField?.value;

    if (livingFromField && livingFromValue && !AppUtils.isYoungerThanTwelveMonths(livingFromValue)) {
      changedFields.push({ name: 'previousPostal', value: undefined });
      changedFields.push({ name: 'previousCity', value: undefined });
      changedFields.push({ name: 'previousStreet', value: undefined });
      changedFields.push({ name: 'previousHouseNumber', value: undefined });
    }
  }

  function scheduleFieldUpdates(
    identification: string,
    changedFields: ICreditRequestUpdate[],
    forcedTimeout?: string,
  ) {
    if (forcedTimeout) {
      clearTimeout(timeouts.current[forcedTimeout]);
      timeouts.current[forcedTimeout] = setTimeout(() => {
        CreditService.updateRequestMultiple(identification, changedFields);
      }, 500);
    } else if (changedFields.length === 1) {
      const obj = changedFields[0];
      clearTimeout(timeouts.current[obj.name]);
      timeouts.current[obj.name] = setTimeout(() => {
        CreditService.updateRequestMultiple(identification, [obj]);
      }, 500);
    } else {
      CreditService.updateRequestMultiple(identification, changedFields);
    }
  }

  const submitRequest = useCallback(() => {
    if (isEditable) {
      CreditService.submitRequest(form.values.identification);
    } else {
      setCurrentCredit({
        id: form.values.id,
        creditAmount: form.values.creditAmount,
        paymentPeriod: form.values.paymentPeriod,
        monthlyInstallmentTo: form.values.monthlyInstallmentTo,
        status: form.values.status,
        requestedAt: form.values.requestedAt,
        user: form.values.user,
      });
      navigate(`${ROUTE_CREDIT_REQUEST_DOCUMENTATION}${location.search}`);
    }
  }, [form, isEditable]);

  if (AppUtils.isEmpty(form.values)) {
    return <></>;
  }

  return (
    <>
      <CreditRequestInfoData
        creditAmount={form.values.creditAmount}
        paymentPeriod={form.values.paymentPeriod}
        monthlyInstallmentTo={form.values.monthlyInstallmentTo}
        t={t}
      />
      <SectionTitle title={t(`credit.step.${CREDIT_REQUEST_STEP_FORM}.title`)} variant={'dark'} pb={'lg'} pt={'xl'} />
      <Stepper
        numOfSteps={CREDIT_REQUEST_FORM_STEPS}
        setActiveStep={setActiveFormStep}
        titleKey={'credit.form.step'}
        t={t}
        withDivider={false}
        activeStep={activeFormStep}
        steps={[
          <CreditInformationStep form={form} options={options!} isEditable={isEditable} onSubmit={() => (setActiveFormStep(old => old + 1))} t={t} />,
          <PersonalInformationStep form={form} options={options!} isEditable={isEditable} onSubmit={() => (setActiveFormStep(old => old + 1))} t={t} setValues={setValues} />,
          <IncomeInformationStep form={form} options={options!} isEditable={isEditable} onSubmit={() => (setActiveFormStep(old => old + 1))} t={t} />,
          <OutcomeInformationStep form={form} options={options!} isEditable={isEditable} onSubmit={submitRequest} t={t} />
        ]}
      />
    </>
  )
}

export default CreditRequestFormPage;